import { Button } from '@flywire/headlessui-tailwindcss';
import {
  CardElement,
  type CardElementError,
  type CardElementEvent,
  type CardElementRef,
  type OnBinLookupResult,
  OnSuccessPayload,
  PayerElement,
} from '@flywire/internal-react-elements';
import { LoadingDots } from 'components/ui/LoadingDots/LoadingDots';
import { FLYWIRE_JS_ENVIRONMENT } from 'constants/config';
import { PropsWithChildren, RefObject } from 'react';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';

import '@flywire/internal-react-elements/styles.css';
import type { SenderFields } from 'domain/recurringReceivables/repository/types';
import { usePayerInfo } from 'hooks/usePayerInfo';

export type CardFormProps = {
  cardFormRef: RefObject<CardElementRef>;
  currency?: string;
  sessionId: string;
  onBinLookup?: (result: OnBinLookupResult) => void;
  onError?: (error: CardElementError) => void;
  onSuccess: (data: OnSuccessPayload) => void;
  onStartLoading: () => void;
  onEndLoading: () => void;
  onEvent?: (event: CardElementEvent) => void;
  senderFields?: SenderFields;
};

const CardForm = ({
  cardFormRef,
  currency,
  children,
  sessionId,
  onBinLookup,
  onError,
  onSuccess,
  onEvent,
  onStartLoading,
  onEndLoading,
  senderFields,
}: PropsWithChildren<CardFormProps>) => {
  const { payerInfo } = usePayerInfo(senderFields);

  const _onError = async (error: CardElementError) => {
    switch (error.type) {
      case 'challenge_error':
      case 'invalid_card_info':
      case 'invalid_payer_info':
      case 'not_found':
        break;
      case 'internal_error':
        errorNotifier.notify(
          new Error(`CardForm error: ${JSON.stringify(error)}`),
        );
    }

    onEndLoading();
    onError?.(error);
  };

  return (
    <CardElement
      currency={currency}
      environment={FLYWIRE_JS_ENVIRONMENT}
      onBinLookup={onBinLookup}
      onEndLoading={onEndLoading}
      onError={_onError}
      onStartLoading={onStartLoading}
      onSuccess={onSuccess}
      payerInfo={payerInfo}
      sessionId={sessionId}
      onEvent={onEvent}
      ref={cardFormRef}
    >
      {children}
    </CardElement>
  );
};

const Form = ({
  buttonDisabled = false,
  buttonLabel,
  isLoading = false,
  onSubmit,
}: {
  buttonDisabled?: boolean;
  buttonLabel: string;
  isLoading?: boolean;
  onSubmit?: () => void;
}) => {
  const disabled = isLoading || buttonDisabled;

  return (
    <div className="flex flex-col mt-8 gap-8">
      <PayerElement className="grid md:grid-cols-2 !grid-cols-1 md:col-span-2">
        <PayerElement.FirstName />
        <PayerElement.LastName />
      </PayerElement>
      <CardElement.CardNumber className="col-span-2" />
      <CardElement.ExpiryDate />
      <CardElement.CVV />
      <Button
        data-testid="cardFormSubmitBtn"
        onClick={onSubmit}
        disabled={disabled}
      >
        {isLoading ? (
          <LoadingDots className="h-6 items-center [&>span]:bg-white" />
        ) : (
          buttonLabel
        )}
      </Button>
    </div>
  );
};

CardForm.Form = Form;

export { CardForm };
